import { Box, Button, Tabs, Tooltip } from "@mantine/core"
import { FileTextIcon } from "@phosphor-icons/react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { DropShippingApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { DropShippingMode, SupplierDropShipping, SupplierDropShippingRef } from "../../components/supplier-dropshipping/list"
import { ReasonTooltip } from "../../components/ui"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"

/** Tab -> list mode. Each tab has its own permission (BE order/dropshipping/list.ts accepts either). */
const TAB_MODE: Record<string, DropShippingMode> = { SOLD_BY_PARTNER: "partner", SOLD_BY_ME: "mine" }

export function SupplierDropShippingTabs() {
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_DRSHPING = usePermission("ORD_DRSHPING")
  const ORD_DROPPED = usePermission("ORD_DROPPED")
  const [activeTab, setActiveTab] = useState<string>(ORD_DRSHPING.hasAccess || !ORD_DROPPED.hasAccess ? "SOLD_BY_PARTNER" : "SOLD_BY_ME")
  const [excelBusy, setExcelBusy] = useState(false)
  const listRef = useRef<SupplierDropShippingRef>(null)

  const activePerm = activeTab === "SOLD_BY_PARTNER" ? ORD_DRSHPING : ORD_DROPPED

  const onExcel = async () => {
    setExcelBusy(true)
    try {
      const { url } = await DropShippingApi.excel({ filter: listRef.current?.getFilter(), order: listRef.current?.getOrder() })
      window.open(url, "_blank")
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setExcelBusy(false)
    }
  }

  // A tab without its permission stays visible and aria-disabled; opening it shows the «Эрх хэрэгтэй» card.
  const tab = (value: string, label: string, perm: typeof ORD_DRSHPING) => (
    <Tooltip label={perm.reason} disabled={perm.hasAccess} withArrow>
      <Tabs.Tab value={value} aria-disabled={!perm.hasAccess || undefined}>{label}</Tabs.Tab>
    </Tooltip>
  )

  return (
    <PageLayout
      title={t("supplierSales:dropShipping.title")}
      subTitle={t("supplierSales:dropShipping.subtitle")}
      breadcrumb={[{ to: "/", label: t("common:breadcrumb.home") }, { label: t("supplierSales:dropShipping.breadcrumb") }]}
      extra={[
        <ReasonTooltip key="excel" reason={activePerm.reason}>
          <Button variant="default" leftSection={<FileTextIcon size={16} />} loading={excelBusy} onClick={onExcel}>
            {t("supplierSales:dropShipping.fileLabel")}
          </Button>
        </ReasonTooltip>,
      ]}>
      <Tabs keepMounted={false} value={activeTab} onChange={(v) => setActiveTab(v!)}>
        <Tabs.List>
          {tab("SOLD_BY_PARTNER", t("supplierSales:dropShipping.tabFromUs"), ORD_DRSHPING)}
          {tab("SOLD_BY_ME", t("supplierSales:dropShipping.tabSupplier"), ORD_DROPPED)}
        </Tabs.List>
        {Object.entries(TAB_MODE).map(([value, mode]) => {
          const perm = value === "SOLD_BY_PARTNER" ? ORD_DRSHPING : ORD_DROPPED
          return (
            <Tabs.Panel key={value} value={value} mt="md">
              {perm.hasAccess ? <SupplierDropShipping ref={listRef} mode={mode} /> : <Box>{perm.accessDenied()}</Box>}
            </Tabs.Panel>
          )
        })}
      </Tabs>
    </PageLayout>
  )
}
